import React from "react";
import { Mail, MessageSquare, Clock, Phone, MessageCircle, MapPin, Linkedin } from "lucide-react";
import EarlyAccessForm from "@/components/EarlyAccessForm";
import Seo from "@/components/Seo";
import { CONTACT, FOUNDER } from "@/data/company";

const POINTS = [
  { icon: Mail, title: "Email us", text: CONTACT.email, href: `mailto:${CONTACT.email}`, tid: "contact-email-link" },
  { icon: Phone, title: "Call us", text: CONTACT.phone, href: CONTACT.phoneHref, tid: "contact-phone-link" },
  { icon: MessageCircle, title: "WhatsApp business support", text: CONTACT.phone, href: CONTACT.whatsapp, external: true, tid: "contact-whatsapp-link" },
  { icon: MapPin, title: "Location", text: CONTACT.location },
  { icon: Clock, title: "Response time", text: `We usually reply ${CONTACT.responseTime}.` },
];

export default function ContactPage() {
  return (
    <main data-testid="contact-page" className="pt-24">
      <Seo path="/contact" />
      {/* Header */}
      <section className="relative overflow-hidden bg-[#091630] py-16 sm:py-20">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#21c55e]/15 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-[#06b6d4]/15 blur-3xl" />
        <div className="absolute inset-0 dotted-bg opacity-[0.12]" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold text-[#21c55e]">
            <MessageSquare className="h-3.5 w-3.5" /> Contact
          </span>
          <h1 className="font-display mt-5 text-4xl sm:text-5xl font-extrabold tracking-tight text-white leading-[1.05]">
            Talk to the WorkPay team.
          </h1>
          <p className="mt-4 text-base text-slate-300 max-w-2xl leading-relaxed">
            Questions about project payments, milestones, payouts or getting started? Send us a message and
            we'll get back to you.
          </p>
        </div>
      </section>

      <section className="relative py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[0.8fr_1.2fr] gap-10 items-start">
          {/* Left info */}
          <div className="space-y-4">
            {POINTS.map((p) => (
              <div key={p.title} data-testid={`contact-point-${p.title.toLowerCase().replace(/\s+/g, "-")}`} className="flex gap-4 rounded-3xl border border-slate-100 bg-white soft-shadow p-5 sm:p-6">
                <div className="h-11 w-11 rounded-2xl bg-[#ecfdf3] flex items-center justify-center shrink-0">
                  <p.icon className="h-5 w-5 text-[#16a34a]" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display text-base font-bold text-[#091630] mb-1">{p.title}</h3>
                  {p.href ? (
                    <a
                      href={p.href}
                      data-testid={p.tid}
                      {...(p.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                      className="text-[15px] font-semibold text-[#16a34a] break-words"
                    >
                      {p.text}
                    </a>
                  ) : (
                    <p className="text-[15px] text-slate-600 leading-relaxed">{p.text}</p>
                  )}
                </div>
              </div>
            ))}
            <a
              href={FOUNDER.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-founder-linkedin"
              className="flex gap-4 rounded-3xl border border-slate-100 bg-white soft-shadow p-5 sm:p-6 hover:border-slate-300 transition-colors"
            >
              <div className="h-11 w-11 rounded-2xl bg-[#eef4ff] flex items-center justify-center shrink-0">
                <Linkedin className="h-5 w-5 text-[#0a66c2]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-display text-base font-bold text-[#091630] mb-1">Founder on LinkedIn</h3>
                <span className="text-[15px] font-semibold text-[#16a34a] break-words">Connect with {FOUNDER.name}</span>
              </div>
            </a>
          </div>

          {/* Form */}
          <div>
            <h2 className="font-display text-xl font-bold text-[#091630] mb-4">Send us a message</h2>
            <EarlyAccessForm source="contact" />
          </div>
        </div>
      </section>
    </main>
  );
}
